import type { ListMonitoringEntriesQueryControllerListCheckSlug } from "@/generated/model/listMonitoringEntriesQueryControllerListCheckSlug";
import type { ListMonitoringEntriesQueryControllerListParams } from "@/generated/model/listMonitoringEntriesQueryControllerListParams";
import type { ListMonitoringEntriesQueryControllerListScope } from "@/generated/model/listMonitoringEntriesQueryControllerListScope";
import type { ListMonitoringEntriesQueryControllerListSortBy } from "@/generated/model/listMonitoringEntriesQueryControllerListSortBy";
import type { ListMonitoringEntriesQueryControllerListSortDir } from "@/generated/model/listMonitoringEntriesQueryControllerListSortDir";
import type { MonitoringEntryDto } from "@/generated/model/monitoringEntryDto";
import { useListMonitoringEntriesQueryControllerList } from "@/generated/monitoring/monitoring";
import type { MonitoringSortColumn, SortDir } from "@/lib/monitoring";

interface UseMonitoringDataArgs {
  nurseryId?: string;
  /** Resolve with `monitoringScopeFor(tab, view)` — History spans two scopes. */
  scope: ListMonitoringEntriesQueryControllerListScope;
  logTypeId?: string;
  /** Slug of a pass/fail check — how non-LogType checks are filtered. */
  checkSlug?: string;
  locationId?: string;
  sortBy?: MonitoringSortColumn;
  sortDir: SortDir;
  page: number;
  pageSize: number;
}

/**
 * Drives one Monitoring view server-side: the caller resolves its tab and
 * sub-view down to an API scope (open alerts, resolved alerts, or readings),
 * with log type, location, sort and pagination forwarded as query params.
 * The Alerts tab badge comes back on every response, and the previous
 * response is held as placeholder data while a new view/filter/page loads so
 * the badge (and table) don't flash empty between requests.
 */
export function useMonitoringData({
  nurseryId,
  scope,
  logTypeId,
  checkSlug,
  locationId,
  sortBy,
  sortDir,
  page,
  pageSize,
}: UseMonitoringDataArgs) {
  const params: ListMonitoringEntriesQueryControllerListParams = {
    scope,
    logTypeId: logTypeId || undefined,
    checkSlug: (checkSlug || undefined) as
      | ListMonitoringEntriesQueryControllerListCheckSlug
      | undefined,
    locationId: locationId || undefined,
    sortBy: sortBy as ListMonitoringEntriesQueryControllerListSortBy,
    sortDir: sortDir as ListMonitoringEntriesQueryControllerListSortDir,
    limit: pageSize,
    offset: page * pageSize,
  };

  const listQuery = useListMonitoringEntriesQueryControllerList(
    nurseryId ?? "",
    params,
    {
      query: {
        enabled: !!nurseryId,
        // refetchOnMount "always": the global QueryClient defaults (staleTime
        // 5min, refetchOnMount false) would otherwise replay a cached list
        // after a reading was logged elsewhere — alerts open asynchronously
        // server-side, so this page must refetch on every visit. Same
        // convention as use-compliance-manager-data.
        refetchOnMount: "always",
        // staleTime 0 because refetchOnMount can't cover this page: tab and
        // sub-view are search params on one route, so switching scope never
        // remounts and the mount refetch fires only once. Without it, a scope
        // resolved elsewhere replays up to five minutes stale. Cached rows
        // still render instantly; the refetch corrects them behind that.
        staleTime: 0,
        placeholderData: (previousData) => previousData,
      },
    },
  );

  const entries: MonitoringEntryDto[] = listQuery.data?.data.data ?? [];
  const total: number = listQuery.data?.data.meta.total ?? 0;
  const activeAlertCount: number = listQuery.data?.data.activeAlertCount ?? 0;

  return {
    entries,
    total,
    activeAlertCount,
    isLoading: listQuery.isLoading,
    // `placeholderData` keeps the previous scope's rows on screen while a new
    // one loads. That is right for a page change, but across a scope change the
    // columns differ — readings rendered under "Resolved"/"Resolved by" would
    // show a table of dashes. Callers gate on this to hold the spinner instead.
    isPlaceholder: listQuery.isPlaceholderData,
  };
}
